import { API, BACKEND_URL } from '@/config/api';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { isDemoUser } from '@/lib/utils';
import AdminNavigation from '@/components/AdminNavigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Loader2, FlaskConical, Plus, Trash2, Edit, ChevronDown, ChevronRight, AlertTriangle, X } from 'lucide-react';

// Import refactored components
import { AddGroupDialog, AddSpeciesDialog, OrganismCard } from './AdminOrganisms';

export default function AdminOrganismsNew() {
  const { user } = useAuth();
  const [algaeStructure, setAlgaeStructure] = useState({});
  const [zooplankton, setZooplankton] = useState({}); // Now hierarchical like algae
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);
  const [showAddSpeciesDialog, setShowAddSpeciesDialog] = useState(false);
  const [showEditSpeciesDialog, setShowEditSpeciesDialog] = useState(false);
  const [showAddZooplanktonDialog, setShowAddZooplanktonDialog] = useState(false);
  const [showEditZooplanktonDialog, setShowEditZooplanktonDialog] = useState(false);
  const [showEditGroupDialog, setShowEditGroupDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  
  // Group threshold fields
  const [groupThresholds, setGroupThresholds] = useState({});
  
  // Form fields (same for both Algae and Zooplankton - hierarchical structure)
  const [groupName, setGroupName] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [species, setSpecies] = useState('');
  const [depth, setDepth] = useState(''); // Only for algae
  
  // Threshold fields
  const [thresholdGreen, setThresholdGreen] = useState('');
  const [thresholdYellow, setThresholdYellow] = useState('');
  const [thresholdOrange, setThresholdOrange] = useState('');
  const [thresholdRed, setThresholdRed] = useState('');
  
  // Biovolume fields
  const [biovolumeSizeClasses, setBiovolumeSizeClasses] = useState([]);
  const [newSizeClassName, setNewSizeClassName] = useState('');
  const [newSizeClassVolume, setNewSizeClassVolume] = useState('');
  
  // Default zero value for zooplankton (e.g. "<1" or "<5")
  const [defaultZeroValue, setDefaultZeroValue] = useState('<5');
  
  // New zooplankton group
  const [showNewZooGroup, setShowNewZooGroup] = useState(false);
  const [newZooGroupName, setNewZooGroupName] = useState('');
  
  // Collapsible states
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const isDemo = isDemoUser(user);
      
      if (isDemo) {
        // Demo mode - load from localStorage or use defaults
        const savedAlgae = localStorage.getItem('demo_organisms_algae');
        const savedZooplankton = localStorage.getItem('demo_organisms_zooplankton');
        
        const defaultAlgae = {
          'Kiselalger': [
            { _id: 'demo-1', species: 'Skeletonema', depth: '3' },
            { _id: 'demo-2', species: 'Chaetoceros', depth: '3' },
            { _id: 'demo-3', species: 'Pseudo-Nitzschia', depth: '3' },
            { _id: 'demo-4', species: 'Div', depth: '3' },
          ],
          'Svepeflagellater': [
            { _id: 'demo-5', species: 'Chryso/Prymn', depth: '5' },
            { _id: 'demo-6', species: 'Phaeocystis', depth: '5' },
          ],
          'Nåleflagellater': [
            { _id: 'demo-10', species: 'Pseudochatonella', depth: '3' }
          ],
          'Kiselflagellater': [
            { _id: 'demo-11', species: 'Dictyocha/Vicicitus', depth: '5' }
          ],
          'Fureflagellater': [
            { _id: 'demo-7', species: 'Gymnodinium/Karenia/Kalodinium', depth: '5' },
            { _id: 'demo-8', species: 'Alexandrium', depth: '5' },
          ]
        };
        
        const defaultZooplankton = {
          'Håvtrekk': [
            { _id: 'demo-z1', species: 'Mikromåneter' },
            { _id: 'demo-z2', species: 'Kolonnmaneter' },
            { _id: 'demo-z3', species: 'Pigghudlarver' },
            { _id: 'demo-z4', species: 'Foraminifera' },
            { _id: 'demo-z5', species: 'Hydroider' },
            { _id: 'demo-z6', species: 'Radiolaria' },
            { _id: 'demo-z7', species: 'Larver skjell' },
          ],
          'Visuelt': [
            { _id: 'demo-z8', species: 'Manet' },
            { _id: 'demo-z9', species: 'Ribbemanet' },
          ]
        };
        
        const algaeData = savedAlgae ? JSON.parse(savedAlgae) : defaultAlgae;
        const zooplanktonData = savedZooplankton ? JSON.parse(savedZooplankton) : defaultZooplankton;
        
        setAlgaeStructure(algaeData);
        setZooplankton(zooplanktonData);
        
        // Save to localStorage if not already saved
        if (!savedAlgae) {
          localStorage.setItem('demo_organisms_algae', JSON.stringify(defaultAlgae));
        }
        if (!savedZooplankton) {
          localStorage.setItem('demo_organisms_zooplankton', JSON.stringify(defaultZooplankton));
        }
      } else {
        // Real mode
        const [algaeRes, zooplanktonRes] = await Promise.all([
          axios.get(`${API}/organisms/algae-structure`, { withCredentials: true }),
          axios.get(`${API}/organisms/zooplankton-structure`, { withCredentials: true })
        ]);
        
        setAlgaeStructure(algaeRes.data);
        setZooplankton(zooplanktonRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Kunne ikke hente organismer');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGroup = async (e) => {
    e.preventDefault();
    
    if (!groupName) {
      toast.error('Gruppenavn er påkrevd');
      return;
    }
    
    const isDemo = isDemoUser(user);
    
    // Create empty group in state first (works for both demo and real mode)
    setAlgaeStructure(prev => ({
      ...prev,
      [groupName]: prev[groupName] || []
    }));
    
    if (isDemo) {
      // Demo mode - save to localStorage as well
      const updatedStructure = {
        ...algaeStructure,
        [groupName]: []
      };
      localStorage.setItem('demo_organisms_algae', JSON.stringify(updatedStructure));
      // Open species dialog with this group pre-selected (same as real mode)
      setSelectedGroup(groupName);
      setShowAddGroupDialog(false);
      setShowAddSpeciesDialog(true);
      toast.success(`Gruppe "${groupName}" opprettet! Legg til første art.`);
      setGroupName('');
      return;
    } else {
      // Real mode - open species dialog with this group pre-selected
      // Groups are created in backend when first species is added
      setSelectedGroup(groupName);
      setShowAddGroupDialog(false);
      setShowAddSpeciesDialog(true);
      toast.success(`Gruppe "${groupName}" opprettet! Legg til første art.`);
      setGroupName('');
      return;
    }
  };

  const handleAddSpecies = async (e) => {
    e.preventDefault();
    
    if (!selectedGroup || !species) {
      toast.error('Gruppe og art er påkrevd');
      return;
    }
    
    setLoading(true);
    
    try {
      const isDemo = isDemoUser(user);
      
      if (isDemo) {
        // Demo mode - update state AND localStorage
        const newSpecies = {
          _id: `demo-${Date.now()}`,
          species,
          depth: depth || ''
        };
        
        const updatedStructure = {
          ...algaeStructure,
          [selectedGroup]: [...(algaeStructure[selectedGroup] || []), newSpecies]
        };
        
        setAlgaeStructure(updatedStructure);
        localStorage.setItem('demo_organisms_algae', JSON.stringify(updatedStructure));
        toast.success('Art lagt til!');
      } else {
        // Real mode
        const data = {
          type: 'algae',
          group: selectedGroup,
          species,
          depth: depth || ''
        };
        
        await axios.post(`${API}/organisms`, data, { withCredentials: true });
        toast.success('Art lagt til!');
        fetchData();
      }
      
      setShowAddSpeciesDialog(false);
      setSelectedGroup('');
      setSpecies('');
      setDepth('');
    } catch (error) {
      console.error('Error adding species:', error);
      toast.error('Kunne ikke legge til art');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSpecies = async (e) => {
    e.preventDefault();
    
    if (!editingItem || !species) {
      toast.error('Art er påkrevd');
      return;
    }
    
    setLoading(true);
    
    try {
      const isDemo = isDemoUser(user);
      
      if (isDemo) {
        // Demo mode - update state AND localStorage
        const isAlgae = editingItem.type === 'algae';
        const targetStructure = isAlgae ? algaeStructure : zooplankton;
        const setTargetStructure = isAlgae ? setAlgaeStructure : setZooplankton;
        const storageKey = isAlgae ? 'demo_organisms_algae' : 'demo_organisms_zooplankton';
        
        const updatedStructure = {
          ...targetStructure,
          [editingItem.group]: targetStructure[editingItem.group].map(sp =>
            sp._id === editingItem._id
              ? { 
                  ...sp, 
                  species, 
                  ...(isAlgae && { depth: depth || '' }),
                  ...(!isAlgae && { default_value: defaultZeroValue || '<5' }),
                  threshold_green: thresholdGreen || '',
                  threshold_yellow: thresholdYellow || '',
                  threshold_orange: thresholdOrange || '',
                  threshold_red: thresholdRed || '',
                  biovolume_classes: biovolumeSizeClasses
                }
              : sp
          )
        };
        
        setTargetStructure(updatedStructure);
        localStorage.setItem(storageKey, JSON.stringify(updatedStructure));
        toast.success('Art oppdatert!');
      } else {
        // Real mode
        const data = {
          type: editingItem.type,
          group: editingItem.group,
          species,
          ...(editingItem.type === 'algae' && { depth: depth || '' }),
          ...(editingItem.type === 'zooplankton' && { default_value: defaultZeroValue || '<5' }),
          threshold_green: 0,  // Always 0 - green is from 0 to yellow threshold
          threshold_yellow: parseFloat(thresholdYellow) || 0,
          threshold_orange: parseFloat(thresholdOrange) || 0,
          threshold_red: parseFloat(thresholdRed) || 0,
          biovolume_classes: biovolumeSizeClasses
        };
        
        const response = await axios.put(`${API}/organisms/${editingItem._id}`, data, { withCredentials: true });
        toast.success('Art oppdatert!');
        await fetchData(); // Wait for data to reload
      }
      
      setShowEditSpeciesDialog(false);
      setEditingItem(null);
      setSpecies('');
      setDepth('');
    } catch (error) {
      console.error('Error updating species:', error);
      toast.error('Kunne ikke oppdatere art');
    } finally {
      setLoading(false);
    }
  };

  // Add biovolume size class
  const addBiovolumeSizeClass = () => {
    if (!newSizeClassName || !newSizeClassVolume) {
      toast.error('Klassenavn og biovolum er påkrevd');
      return;
    }
    
    const newClass = {
      id: `class-${Date.now()}`,
      name: newSizeClassName,
      biovolume: parseFloat(newSizeClassVolume) || 0
    };
    
    setBiovolumeSizeClasses(prev => [...prev, newClass]);
    setNewSizeClassName('');
    setNewSizeClassVolume('');
  };

  // Remove biovolume size class
  const removeBiovolumeSizeClass = (idOrName) => {
    setBiovolumeSizeClasses(prev => prev.filter(c => c.id !== idOrName && c.name !== idOrName));
  };

  const handleAddZooplankton = async (e) => {
    e.preventDefault();
    
    if (!selectedGroup || !species) {
      toast.error('Prøvetype (gruppe) og organisme (art) er påkrevd');
      return;
    }
    
    setLoading(true);
    
    try {
      const isDemo = isDemoUser(user);
      
      if (isDemo) {
        // Demo mode - hierarchical structure
        const newZoo = {
          _id: `demo-z${Date.now()}`,
          species
        };
        
        setZooplankton(prev => ({
          ...prev,
          [selectedGroup]: [...(prev[selectedGroup] || []), newZoo]
        }));
        toast.success('Zooplankton lagt til!');
      } else {
        // Real mode
        const data = {
          type: 'zooplankton',
          group: selectedGroup,  // Prøvetype as group
          species  // Organism as species
        };
        
        await axios.post(`${API}/organisms`, data, { withCredentials: true });
        toast.success('Zooplankton lagt til!');
        fetchData();
      }
      
      setShowAddZooplanktonDialog(false);
      setSelectedGroup('');
      setSpecies('');
      setShowNewZooGroup(false);
      setNewZooGroupName('');
    } catch (error) {
      console.error('Error adding zooplankton:', error);
      toast.error('Kunne ikke legge til zooplankton');
    } finally {
      setLoading(false);
    }
  };

  const handleEditZooplankton = async (e) => {
    e.preventDefault();
    
    if (!editingItem || !species) {
      toast.error('Organisme (art) er påkrevd');
      return;
    }
    
    setLoading(true);
    
    try {
      const isDemo = isDemoUser(user);
      
      if (isDemo) {
        // Demo mode - hierarchical structure
        setZooplankton(prev => ({
          ...prev,
          [editingItem.group]: prev[editingItem.group].map(z =>
            z._id === editingItem._id
              ? { ...z, species }
              : z
          )
        }));
        toast.success('Zooplankton oppdatert!');
      } else {
        // Real mode
        const data = {
          type: 'zooplankton',
          group: editingItem.group,
          species
        };
        
        await axios.put(`${API}/organisms/${editingItem._id}`, data, { withCredentials: true });
        toast.success('Zooplankton oppdatert!');
        fetchData();
      }
      
      setShowEditZooplanktonDialog(false);
      setEditingItem(null);
      setSpecies('');
    } catch (error) {
      console.error('Error updating zooplankton:', error);
      toast.error('Kunne ikke oppdatere zooplankton');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteZooplankton = async (group, zooId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne zooplankton?')) return;
    
    setLoading(true);
    try {
      const isDemo = isDemoUser(user);
      
      if (isDemo) {
        setZooplankton(prev => ({
          ...prev,
          [group]: prev[group].filter(z => z._id !== zooId)
        }));
        toast.success('Zooplankton slettet!');
      } else {
        await axios.delete(`${API}/organisms/${zooId}`, { withCredentials: true });
        toast.success('Zooplankton slettet!');
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting zooplankton:', error);
      toast.error('Kunne ikke slette zooplankton');
    } finally {
      setLoading(false);
    }
  };

  const openEditSpeciesDialog = (group, sp, type = 'algae') => {
    setEditingItem({ ...sp, group, type });
    setSpecies(sp.species);
    setDepth(sp.depth || '');
    // Use ?? (nullish coalescing) instead of || to preserve 0 values
    setThresholdGreen(sp.threshold_green ?? '');
    setThresholdYellow(sp.threshold_yellow ?? '');
    setThresholdOrange(sp.threshold_orange ?? '');
    setThresholdRed(sp.threshold_red ?? '');
    // Load biovolume size classes - use defaults if none defined
    const defaultClasses = [
      { id: 'liten', name: 'Liten', biovolume: 100 },
      { id: 'median', name: 'Median', biovolume: 500 },
      { id: 'stor', name: 'Stor', biovolume: 2000 }
    ];
    setBiovolumeSizeClasses(sp.biovolume_classes?.length > 0 ? sp.biovolume_classes : defaultClasses);
    setShowEditSpeciesDialog(true);
  };

  const openEditZooplanktonDialog = (group, zoo) => {
    setEditingItem({ ...zoo, group, type: 'zooplankton' });
    setSpecies(zoo.species);
    // Use ?? (nullish coalescing) instead of || to preserve 0 values
    setThresholdGreen(zoo.threshold_green ?? '');
    setThresholdYellow(zoo.threshold_yellow ?? '');
    setThresholdOrange(zoo.threshold_orange ?? '');
    setThresholdRed(zoo.threshold_red ?? '');
    setDefaultZeroValue(zoo.default_value || '<5');
    // Load biovolume size classes - use defaults if none defined
    const defaultClasses = [
      { id: 'liten', name: 'Liten', biovolume: 100 },
      { id: 'median', name: 'Median', biovolume: 500 },
      { id: 'stor', name: 'Stor', biovolume: 2000 }
    ];
    setBiovolumeSizeClasses(zoo.biovolume_classes?.length > 0 ? zoo.biovolume_classes : defaultClasses);
    setShowEditSpeciesDialog(true);  // Use same dialog
  };

  // Open dialog to edit group-level thresholds
  const openEditGroupDialog = (groupName, currentThresholds) => {
    setEditingGroupName(groupName);
    setGroupThresholds({
      threshold_yellow: currentThresholds.threshold_yellow || '',
      threshold_orange: currentThresholds.threshold_orange || '',
      threshold_red: currentThresholds.threshold_red || ''
    });
    setShowEditGroupDialog(true);
  };

  // Save group thresholds
  const handleSaveGroupThresholds = async (e) => {
    e.preventDefault();
    
    if (!editingGroupName) return;
    
    setLoading(true);
    try {
      const isDemo = isDemoUser(user);
      
      if (isDemo) {
        toast.success('Gruppeterskelverdi lagret (demo-modus)');
      } else {
        await axios.put(`${API}/organisms/group-thresholds/${encodeURIComponent(editingGroupName)}`, {
          threshold_yellow: parseFloat(groupThresholds.threshold_yellow) || 0,
          threshold_orange: parseFloat(groupThresholds.threshold_orange) || 0,
          threshold_red: parseFloat(groupThresholds.threshold_red) || 0
        }, { withCredentials: true });
        
        toast.success(`Grenseverdier for ${editingGroupName} Sum oppdatert!`);
        fetchData(); // Refresh to show updated values
      }
      
      setShowEditGroupDialog(false);
      setEditingGroupName('');
      setGroupThresholds({});
    } catch (error) {
      console.error('Error saving group thresholds:', error);
      toast.error('Kunne ikke lagre grenseverdier');
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteSpecies = async (group, speciesId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne arten?')) return;
    
    setLoading(true);
    try {
      const isDemo = isDemoUser(user);
      
      if (isDemo) {
        const updatedStructure = {
          ...algaeStructure,
          [group]: algaeStructure[group].filter(s => s._id !== speciesId)
        };
        setAlgaeStructure(updatedStructure);
        localStorage.setItem('demo_organisms_algae', JSON.stringify(updatedStructure));
        toast.success('Art slettet!');
      } else {
        await axios.delete(`${API}/organisms/${speciesId}`, { withCredentials: true });
        toast.success('Art slettet!');
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting species:', error);
      toast.error('Kunne ikke slette art');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    const speciesCount = algaeStructure[group]?.length || 0;
    if (speciesCount > 0) {
      toast.error(`Kan ikke slette gruppe med ${speciesCount} arter. Slett artene først.`);
      return;
    }
    
    if (!window.confirm(`Er du sikker på at du vil slette gruppen "${group}"?`)) return;
    
    const isDemo = isDemoUser(user);
    
    if (isDemo) {
      setAlgaeStructure(prev => {
        const newStructure = { ...prev };
        delete newStructure[group];
        return newStructure;
      });
      toast.success('Gruppe slettet!');
    } else {
      toast.info('Tomme grupper slettes automatisk');
    }
  };

  const toggleGroup = (group) => {
    setOpenGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-blue-50/20 flex">
      <AdminNavigation />
      
      <main className="flex-1 p-4 lg:p-8 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Organismer</h1>
              <p className="text-sm text-slate-600 mt-1">Administrer algegrupper, arter og zooplankton</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
          {/* Algae Card */}
          <Card className="overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <CardTitle className="flex items-center space-x-2 text-teal-700 text-base sm:text-lg">
                  <FlaskConical className="w-5 h-5 flex-shrink-0" />
                  <span>Alger</span>
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                  <Dialog open={showAddGroupDialog} onOpenChange={setShowAddGroupDialog}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" className="border-teal-300 text-teal-700 text-xs sm:text-sm">
                        <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        Ny gruppe
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-[95vw] sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Legg til ny gruppe</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddGroup}>
                        <div className="space-y-4">
                          <div>
                            <Label>Gruppenavn *</Label>
                            <Input
                              value={groupName}
                              onChange={(e) => setGroupName(e.target.value)}
                              placeholder="F.eks. Kiselalger"
                              required
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              Gruppa vert oppretta når du legg til første art
                            </p>
                          </div>
                        </div>
                        <DialogFooter className="mt-4">
                          <Button type="button" variant="outline" onClick={() => setShowAddGroupDialog(false)}>
                            Avbryt
                          </Button>
                          <Button type="submit" className="bg-teal-600 hover:bg-teal-700">Neste: Legg til art</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                  
                  <Dialog open={showAddSpeciesDialog} onOpenChange={(open) => { setShowAddSpeciesDialog(open); if (!open) { setSelectedGroup(''); setSpecies(''); setDepth(''); } }}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-xs sm:text-sm">
                        <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        Ny art
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-lg font-bold text-slate-900">Legg til ny art</DialogTitle>
                        <p className="text-sm text-slate-500">Vel gruppe og skriv inn artsinformasjon</p>
                      </DialogHeader>
                      <form onSubmit={handleAddSpecies}>
                        <div className="space-y-5 py-2">
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-slate-700">Gruppe *</Label>
                            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                              {Object.keys(algaeStructure).map(group => (
                                <button
                                  key={group}
                                  type="button"
                                  onClick={() => setSelectedGroup(group)}
                                  className={`px-3 py-2.5 rounded-lg text-sm font-medium border text-left transition-all ${
                                    selectedGroup === group
                                      ? 'bg-teal-50 border-teal-400 text-teal-700 ring-1 ring-teal-400'
                                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                                  }`}
                                >
                                  {group}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2 space-y-2">
                              <Label className="text-sm font-medium text-slate-700">Art *</Label>
                              <Input
                                value={species}
                                onChange={(e) => setSpecies(e.target.value)}
                                placeholder="F.eks. Skeletonema"
                                required
                                className="h-11"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm font-medium text-slate-700">Djup (m)</Label>
                              <Input
                                type="number"
                                value={depth}
                                onChange={(e) => setDepth(e.target.value)}
                                placeholder="3"
                                className="h-11"
                              />
                            </div>
                          </div>
                        </div>
                        <DialogFooter className="mt-5 gap-2">
                          <Button type="button" variant="outline" onClick={() => setShowAddSpeciesDialog(false)}>
                            Avbryt
                          </Button>
                          <Button type="submit" disabled={!selectedGroup || !species} className="bg-teal-600 hover:bg-teal-700">
                            <Plus className="w-4 h-4 mr-1" />
                            Legg til
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {loading ? (
                <p className="text-center text-slate-500">Laster...</p>
              ) : Object.keys(algaeStructure).length === 0 ? (
                <p className="text-center text-slate-500">Ingen grupper funnet</p>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {Object.entries(algaeStructure).map(([group, groupData]) => {
                    // Handle both old format (array) and new format (object with species array)
                    const speciesList = Array.isArray(groupData) ? groupData : (groupData.species || []);
                    const groupThresholds = Array.isArray(groupData) ? {} : (groupData.group_thresholds || {});
                    
                    return (
                    <Collapsible
                      key={group}
                      open={openGroups[group]}
                      onOpenChange={() => toggleGroup(group)}
                    >
                      <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <CollapsibleTrigger className="w-full">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-2 sm:p-3 bg-teal-50 hover:bg-teal-100 transition-colors gap-2">
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 min-w-0">
                              {openGroups[group] ? (
                                <ChevronDown className="w-4 h-4 text-teal-600 flex-shrink-0" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-teal-600 flex-shrink-0" />
                              )}
                              <span className="font-semibold text-teal-900 text-sm sm:text-base truncate">{group}</span>
                              <span className="text-xs text-teal-600 flex-shrink-0">({speciesList.length})</span>
                              {/* Group thresholds badges - stack on mobile */}
                              {(groupThresholds.threshold_yellow || groupThresholds.threshold_orange || groupThresholds.threshold_red) && (
                                <div className="flex flex-wrap items-center gap-1">
                                  <span className="text-xs text-slate-500 hidden sm:inline">Sum:</span>
                                  {groupThresholds.threshold_yellow > 0 && (
                                    <span className="px-1 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] sm:text-xs rounded">{groupThresholds.threshold_yellow.toLocaleString()}</span>
                                  )}
                                  {groupThresholds.threshold_orange > 0 && (
                                    <span className="px-1 py-0.5 bg-orange-100 text-orange-700 text-[10px] sm:text-xs rounded">{groupThresholds.threshold_orange.toLocaleString()}</span>
                                  )}
                                  {groupThresholds.threshold_red > 0 && (
                                    <span className="px-1 py-0.5 bg-red-100 text-red-700 text-[10px] sm:text-xs rounded">{groupThresholds.threshold_red.toLocaleString()}</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 ml-auto sm:ml-0">
                              {/* Edit group thresholds button */}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditGroupDialog(group, groupThresholds);
                                }}
                                className="text-teal-600 hover:text-teal-700 hover:bg-teal-100 h-7 w-7 p-0"
                                title="Rediger grenseverdier for gruppe"
                              >
                                <AlertTriangle className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteGroup(group);
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="p-2 space-y-1 bg-white">
                            {speciesList.length === 0 ? (
                              <p className="text-center text-slate-400 py-2 text-sm">Ingen arter</p>
                            ) : (
                              speciesList.map((sp) => (
                                <div
                                  key={sp._id}
                                  className="flex items-center justify-between p-3 hover:bg-slate-50 rounded border-b border-slate-100"
                                >
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-900">
                                      {sp.species}
                                    </p>
                                    <div className="flex items-center space-x-2 mt-1">
                                      {sp.depth && (
                                        <span className="text-xs text-slate-400">Dybde: {sp.depth}m</span>
                                      )}
                                      {(sp.threshold_yellow || sp.threshold_orange || sp.threshold_red) && (
                                        <div className="flex items-center space-x-1">
                                          {sp.threshold_yellow && (
                                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">≤{sp.threshold_yellow.toLocaleString()}</span>
                                          )}
                                          {sp.threshold_orange && (
                                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">≤{sp.threshold_orange.toLocaleString()}</span>
                                          )}
                                          {sp.threshold_red && (
                                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">&gt;{sp.threshold_red.toLocaleString()}</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex space-x-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openEditSpeciesDialog(group, sp)}
                                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    >
                                      <Edit className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteSpecies(group, sp._id)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  )})}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dialog for editing group thresholds */}
          <Dialog open={showEditGroupDialog} onOpenChange={setShowEditGroupDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <span>Grenseverdier for {editingGroupName} Sum</span>
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSaveGroupThresholds}>
                <div className="space-y-4 py-4">
                  <p className="text-sm text-slate-600">
                    Disse grenseverdiene brukes for å evaluere SUM av alle arter i gruppen.
                  </p>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-yellow-600 font-medium flex items-center space-x-1">
                        <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
                        <span>Gul (≤)</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="1000000"
                        value={groupThresholds.threshold_yellow || ''}
                        onChange={(e) => setGroupThresholds(prev => ({
                          ...prev,
                          threshold_yellow: e.target.value
                        }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-orange-600 font-medium flex items-center space-x-1">
                        <span className="w-3 h-3 rounded-full bg-orange-400"></span>
                        <span>Oransje (≤)</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="5000000"
                        value={groupThresholds.threshold_orange || ''}
                        onChange={(e) => setGroupThresholds(prev => ({
                          ...prev,
                          threshold_orange: e.target.value
                        }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-red-600 font-medium flex items-center space-x-1">
                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                        <span>Rød (&gt;)</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="10000000"
                        value={groupThresholds.threshold_red || ''}
                        onChange={(e) => setGroupThresholds(prev => ({
                          ...prev,
                          threshold_red: e.target.value
                        }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-500">
                    <p><strong>Eksempel:</strong> For Kiselalger Sum</p>
                    <ul className="mt-1 space-y-0.5">
                      <li>• Grønn: 0 - 999,999 celler/L</li>
                      <li>• Gul: 1,000,000 - 4,999,999 celler/L</li>
                      <li>• Oransje: 5,000,000 - 9,999,999 celler/L</li>
                      <li>• Rød: &gt;10,000,000 celler/L</li>
                    </ul>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowEditGroupDialog(false)}>
                    Avbryt
                  </Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                    Lagre grenseverdier
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Zooplankton Card - Hierarchical like Algae */}
          <Card>
            <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2 text-blue-700">
                  <FlaskConical className="w-5 h-5" />
                  <span>Zooplankton (Hierarkisk struktur)</span>
                </CardTitle>
                <Dialog open={showAddZooplanktonDialog} onOpenChange={(open) => { setShowAddZooplanktonDialog(open); if (!open) { setShowNewZooGroup(false); setNewZooGroupName(''); setSelectedGroup(''); setSpecies(''); } }}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-teal-600 hover:bg-teal-700">
                      <Plus className="w-4 h-4 mr-1" />
                      Ny organisme
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-lg font-bold text-slate-900">Legg til zooplankton</DialogTitle>
                      <p className="text-sm text-slate-500">Vel gruppe og skriv inn organismenamn</p>
                    </DialogHeader>
                    <form onSubmit={handleAddZooplankton}>
                      <div className="space-y-5 py-2">
                        {/* Group selector */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700">Prøvetype (gruppe) *</Label>
                          {showNewZooGroup ? (
                            <div className="flex items-center gap-2">
                              <div className="flex-1 relative">
                                <Input
                                  value={newZooGroupName}
                                  onChange={(e) => {
                                    setNewZooGroupName(e.target.value);
                                    setSelectedGroup(e.target.value);
                                  }}
                                  placeholder="Ny gruppe (f.eks. Håvtrekk)"
                                  autoFocus
                                  className="pl-9"
                                />
                                <Plus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-500" />
                              </div>
                              <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-slate-400 hover:text-slate-600" onClick={() => { setShowNewZooGroup(false); setNewZooGroupName(''); setSelectedGroup(''); }}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-2">
                              {Object.keys(zooplankton).map(group => (
                                <button
                                  key={group}
                                  type="button"
                                  onClick={() => setSelectedGroup(group)}
                                  className={`px-3 py-2.5 rounded-lg text-sm font-medium border text-left transition-all ${
                                    selectedGroup === group
                                      ? 'bg-teal-50 border-teal-400 text-teal-700 ring-1 ring-teal-400'
                                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                                  }`}
                                >
                                  {group}
                                </button>
                              ))}
                              <button
                                type="button"
                                onClick={() => { setShowNewZooGroup(true); setSelectedGroup(''); }}
                                className="px-3 py-2.5 rounded-lg text-sm font-medium border border-dashed border-slate-300 text-slate-400 hover:text-teal-600 hover:border-teal-300 hover:bg-teal-50/50 transition-all flex items-center gap-1.5"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Ny gruppe
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {/* Organism name */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700">Organisme *</Label>
                          <Input
                            value={species}
                            onChange={(e) => setSpecies(e.target.value)}
                            placeholder="F.eks. Mikromåneter"
                            required
                            className="h-11"
                          />
                        </div>
                      </div>
                      <DialogFooter className="mt-5 gap-2">
                        <Button type="button" variant="outline" onClick={() => setShowAddZooplanktonDialog(false)}>
                          Avbryt
                        </Button>
                        <Button type="submit" disabled={(!selectedGroup && !newZooGroupName) || !species} className="bg-teal-600 hover:bg-teal-700">
                          <Plus className="w-4 h-4 mr-1" />
                          Legg til
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {loading ? (
                <p className="text-center text-slate-500">Laster...</p>
              ) : Object.keys(zooplankton).length === 0 ? (
                <p className="text-center text-slate-500">Ingen zooplankton funnet</p>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {Object.entries(zooplankton).map(([group, groupData]) => {
                    // Handle both old format (array) and new format (object with species array)
                    const organismList = Array.isArray(groupData) ? groupData : (groupData.species || []);
                    const groupThresholds = Array.isArray(groupData) ? {} : (groupData.group_thresholds || {});
                    
                    return (
                    <Collapsible
                      key={group}
                      open={openGroups[`zoo_${group}`]}
                      onOpenChange={() => toggleGroup(`zoo_${group}`)}
                    >
                      <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <CollapsibleTrigger className="w-full">
                          <div className="flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 transition-colors">
                            <div className="flex items-center space-x-2">
                              {openGroups[`zoo_${group}`] ? (
                                <ChevronDown className="w-4 h-4 text-blue-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-blue-600" />
                              )}
                              <span className="font-semibold text-blue-900">{group}</span>
                              <span className="text-xs text-blue-600">({organismList.length} organismer)</span>
                              {/* Group thresholds badges */}
                              {(groupThresholds.threshold_yellow || groupThresholds.threshold_orange || groupThresholds.threshold_red) && (
                                <div className="flex items-center space-x-1 ml-2">
                                  <span className="text-xs text-slate-500">Sum:</span>
                                  {groupThresholds.threshold_yellow > 0 && (
                                    <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">≤{groupThresholds.threshold_yellow.toLocaleString()}</span>
                                  )}
                                  {groupThresholds.threshold_orange > 0 && (
                                    <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">≤{groupThresholds.threshold_orange.toLocaleString()}</span>
                                  )}
                                  {groupThresholds.threshold_red > 0 && (
                                    <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded">&gt;{groupThresholds.threshold_red.toLocaleString()}</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center space-x-1">
                              {/* Edit group thresholds button */}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditGroupDialog(group, groupThresholds);
                                }}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                                title="Rediger grenseverdier for gruppe"
                              >
                                <AlertTriangle className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="p-2 space-y-1 bg-white">
                            {organismList.length === 0 ? (
                              <p className="text-center text-slate-400 py-2 text-sm">Ingen organismer</p>
                            ) : (
                              organismList.map((zoo) => (
                                <div
                                  key={zoo._id}
                                  className="flex items-center justify-between p-3 hover:bg-slate-50 rounded border-b border-slate-100"
                                >
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-900">
                                      {zoo.species}
                                    </p>
                                    {(zoo.threshold_green || zoo.threshold_yellow || zoo.threshold_orange || zoo.threshold_red) && (
                                      <div className="flex items-center space-x-1 mt-1">
                                        {zoo.threshold_yellow && (
                                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">≤{zoo.threshold_yellow.toLocaleString()}</span>
                                        )}
                                        {zoo.threshold_orange && (
                                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">≤{zoo.threshold_orange.toLocaleString()}</span>
                                        )}
                                        {zoo.threshold_red && (
                                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">&gt;{zoo.threshold_red.toLocaleString()}</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex space-x-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openEditZooplanktonDialog(group, zoo)}
                                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    >
                                      <Edit className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteZooplankton(group, zoo._id)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  )})}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Edit Species Dialog - Used for both Algae and Zooplankton */}
        <Dialog open={showEditSpeciesDialog} onOpenChange={setShowEditSpeciesDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Rediger {editingItem?.type === 'algae' ? 'alge' : 'zooplankton'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSpecies}>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{editingItem?.type === 'algae' ? 'Gruppe' : 'Prøvetype'}</Label>
                    <Input
                      value={editingItem?.group || ''}
                      disabled
                      className="bg-slate-100"
                    />
                  </div>
                  <div>
                    <Label>{editingItem?.type === 'algae' ? 'Art' : 'Organisme'} *</Label>
                    <Input
                      value={species}
                      onChange={(e) => setSpecies(e.target.value)}
                      placeholder={editingItem?.type === 'algae' ? "F.eks. Skeletonema" : "F.eks. Mikromåneter"}
                      required
                    />
                  </div>
                </div>
                {editingItem?.type === 'algae' && (
                  <div>
                    <Label>Dybde (m)</Label>
                    <Input
                      type="number"
                      value={depth}
                      onChange={(e) => setDepth(e.target.value)}
                      placeholder="F.eks. 3"
                    />
                  </div>
                )}
                
                {editingItem?.type === 'zooplankton' && (
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Standard null-verdi</Label>
                    <p className="text-xs text-slate-500 mb-2">Vert fylt inn automatisk når cella er tom (ved tab)</p>
                    <div className="flex gap-2">
                      {['<1', '<5', '<10'].map(val => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setDefaultZeroValue(val)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                            defaultZeroValue === val
                              ? 'bg-teal-50 border-teal-400 text-teal-700 ring-1 ring-teal-400'
                              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                          }`}
                        >
                          {val}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Threshold fields */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Grenseverdier (ant/l)</h4>
                  <p className="text-xs text-slate-500 mb-3">
                    🟢 Grønn: 0 til gul grense | 🟡 Gul: gul til oransje | 🟠 Oransje: oransje til raud | 🔴 Raud: over raud grense
                  </p>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <Label className="text-xs text-yellow-700 font-medium">Gul grense (fra denne verdien)</Label>
                      <Input
                        type="number"
                        value={thresholdYellow}
                        onChange={(e) => setThresholdYellow(e.target.value)}
                        placeholder="2000000"
                        className="border-yellow-300 focus:border-yellow-500"
                      />
                      <p className="text-xs text-slate-400 mt-1">Grønn: 0 - {thresholdYellow || '0'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-orange-700 font-medium">Oransje grense (fra denne verdien)</Label>
                      <Input
                        type="number"
                        value={thresholdOrange}
                        onChange={(e) => setThresholdOrange(e.target.value)}
                        placeholder="5000000"
                        className="border-orange-300 focus:border-orange-500"
                      />
                      <p className="text-xs text-slate-400 mt-1">Gul: {thresholdYellow || '0'} - {thresholdOrange || '0'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-red-700 font-medium">Raud grense (fra denne verdien)</Label>
                      <Input
                        type="number"
                        value={thresholdRed}
                        onChange={(e) => setThresholdRed(e.target.value)}
                        placeholder="20000000"
                        className="border-red-300 focus:border-red-500"
                      />
                      <p className="text-xs text-slate-400 mt-1">Oransje: {thresholdOrange || '0'} - {thresholdRed || '0'} | Raud: over {thresholdRed || '0'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Biovolume Section */}
                {editingItem?.type === 'algae' && (
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center space-x-2">
                      <span>📊 Biovolum - Størrelses-klasser</span>
                    </h4>
                    <p className="text-xs text-slate-500 mb-3">
                      Definer størrelses-klasser med tilhørende biovolum (µm³/celle)
                    </p>
                    
                    {/* Existing size classes - EDITABLE */}
                    {biovolumeSizeClasses.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {biovolumeSizeClasses.map((cls, index) => (
                          <div key={cls.id || index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border">
                            <div className="flex-1 grid grid-cols-2 gap-2">
                              <Input
                                value={cls.name}
                                onChange={(e) => {
                                  const updated = [...biovolumeSizeClasses];
                                  updated[index] = { ...updated[index], name: e.target.value };
                                  setBiovolumeSizeClasses(updated);
                                }}
                                placeholder="Klassenavn"
                                className="h-8 text-sm font-medium"
                              />
                              <div className="relative">
                                <Input
                                  type="number"
                                  value={cls.biovolume || ''}
                                  onChange={(e) => {
                                    const updated = [...biovolumeSizeClasses];
                                    updated[index] = { ...updated[index], biovolume: parseFloat(e.target.value) || 0 };
                                    setBiovolumeSizeClasses(updated);
                                  }}
                                  placeholder="Biovolum"
                                  className="h-8 text-sm pr-16"
                                />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">µm³/celle</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeBiovolumeSizeClass(cls.id || cls.name)}
                              className="text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add new size class */}
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                        <div>
                          <Label className="text-xs text-blue-700">Klassenavn</Label>
                          <Input
                            value={newSizeClassName}
                            onChange={(e) => setNewSizeClassName(e.target.value)}
                            placeholder="f.eks. Liten, Medium, Stor"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-blue-700">Biovolum (µm³/celle)</Label>
                          <Input
                            type="number"
                            value={newSizeClassVolume}
                            onChange={(e) => setNewSizeClassVolume(e.target.value)}
                            placeholder="f.eks. 500"
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                      <Button 
                        type="button" 
                        size="sm" 
                        onClick={addBiovolumeSizeClass}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Legg til klasse
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter className="mt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditSpeciesDialog(false)}>
                  Avbryt
                </Button>
                <Button type="submit">Lagre endringer</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </main>
    </div>
  );
}
